'use client';

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Pipeline } from '@/lib/types';
import { TEMPLATES } from '@/lib/pipeline/templates';
import { usePipelineStore } from '@/lib/pipeline/store';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmDialog';

export default function PipelineLibraryModal({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (current: { id: number; name: string } | null) => void;
}) {
  const loadGraph = usePipelineStore((s) => s.loadGraph);
  const toast = useToast();
  const { confirm } = useConfirm();
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);

  const refresh = useCallback(async () => {
    try {
      setPipelines(await api.listPipelines());
    } catch {
      /* best-effort */
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openTemplate = (tid: string) => {
    const t = TEMPLATES.find((x) => x.id === tid);
    if (!t) return;
    const g = t.build();
    loadGraph(g.nodes, g.edges);
    onPick(null); // template = unsaved
    toast.success(`Loaded template "${t.name}"`);
    onClose();
  };

  const openPipeline = (p: Pipeline) => {
    try {
      const parsed = JSON.parse(p.graph_json);
      loadGraph(parsed.nodes ?? [], parsed.edges ?? []);
      onPick({ id: p.id, name: p.name });
      toast.success(`Loaded "${p.name}"`);
      onClose();
    } catch {
      toast.error('Could not load pipeline');
    }
  };

  const duplicate = async (p: Pipeline) => {
    await api.createPipeline(`${p.name} (copy)`, p.graph_json, p.description ?? undefined);
    toast.success('Duplicated');
    refresh();
  };

  const remove = async (p: Pipeline) => {
    if (!(await confirm({ message: `Delete pipeline "${p.name}"?`, confirmText: 'Delete', variant: 'danger' })))
      return;
    await api.deletePipeline(p.id);
    refresh();
  };

  const nodeCount = (p: Pipeline) => {
    try {
      return (JSON.parse(p.graph_json).nodes ?? []).length;
    } catch {
      return 0;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="bg-[var(--bg-elevated)] rounded-xl max-w-3xl w-full max-h-[82vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-subtle)]">
          <h2 className="text-lg font-semibold">Pipeline library</h2>
          <button onClick={onClose} className="btn btn-ghost h-8 px-3 text-sm">
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-6">
          {/* Templates */}
          <section>
            <h3 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
              Templates
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => openTemplate(t.id)}
                  className="text-left p-3 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] hover:border-[var(--accent)] transition-subtle"
                >
                  <div className="text-sm font-medium">{t.name}</div>
                  <div className="text-[11px] text-[var(--text-muted)] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </section>

          {/* Saved pipelines */}
          <section>
            <h3 className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wider mb-2">
              Saved pipelines
            </h3>
            {pipelines.length === 0 ? (
              <div className="text-xs text-[var(--text-muted)]">No saved pipelines yet</div>
            ) : (
              <div className="space-y-2">
                {pipelines.map((p) => (
                  <div
                    key={p.id}
                    className="p-3 rounded-lg bg-[var(--bg-surface)] border border-[var(--border-subtle)] flex items-center justify-between gap-3"
                  >
                    <button onClick={() => openPipeline(p)} className="text-left min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {nodeCount(p)} nodes · {new Date(p.updated_at).toLocaleString()}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openPipeline(p)} className="btn btn-ghost h-7 px-2 text-xs">
                        Open
                      </button>
                      <button onClick={() => duplicate(p)} className="btn btn-ghost h-7 px-2 text-xs">
                        Duplicate
                      </button>
                      <button
                        onClick={() => remove(p)}
                        className="btn btn-ghost h-7 px-2 text-xs text-[var(--text-muted)] hover:text-[var(--error)]"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
